import { useEffect, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Divider, IconButton, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  itemPickerApi, warehousesApi, transferOrdersApi,
  type ItemPickerRow,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";

interface DraftLine {
  key: string;
  item: ItemPickerRow | null;
  batchId: string;
  qty: number;
}
const blank = (): DraftLine => ({ key: crypto.randomUUID(), item: null, batchId: "", qty: 0 });

/**
 * UC-M2-TR-01 — create a new transfer. Source warehouse picker drives the
 * item picker so only items with on-hand stock at the source appear.
 *
 * Batch picker isn't yet wired (S8) — for now the user enters a known batch id
 * matching the FEFO suggestion that the picker surfaces; reorder UX polish lands later.
 */
export function CreateTransferOrderPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [sourceWarehouseId, setSourceWarehouseId] = useState(params.get("sourceWarehouseId") ?? "");
  const [destWarehouseId, setDestWarehouseId] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blank()]);

  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
  });

  const picker = useQuery({
    queryKey: ["item-picker", sourceWarehouseId],
    queryFn: () => itemPickerApi.picker({
      stockPolicy: "Source", warehouseId: sourceWarehouseId, pageSize: 50,
    }),
    enabled: !!sourceWarehouseId,
  });

  useEffect(() => {
    // Reset lines when source warehouse changes so picker results stay coherent.
    if (sourceWarehouseId) setLines(prev => prev.map(l => ({ ...l, item: null, batchId: "" })));
  }, [sourceWarehouseId]);

  const update = (key: string, patch: Partial<DraftLine>) =>
    setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));
  const remove = (key: string) =>
    setLines(ls => ls.length === 1 ? ls : ls.filter(l => l.key !== key));

  const validationError =
    !sourceWarehouseId ? "Pick a source warehouse." :
    !destWarehouseId ? "Pick a destination warehouse." :
    sourceWarehouseId === destWarehouseId ? "Source and destination must differ." :
    !lines.every(l => l.item && l.batchId && l.qty > 0) ? "Every line needs an item, a batch and a quantity greater than 0." :
    null;

  const mutation = useMutation({
    meta: { successMessage: "Transfer order created" },
    mutationFn: () => transferOrdersApi.create({
      sourceWarehouseId, destWarehouseId,
      expectedDeliveryDate: expectedDeliveryDate || undefined,
      notes: notes || undefined,
      lines: lines.map(l => ({
        itemId: l.item!.id, batchId: l.batchId, qty: l.qty,
      })),
    }),
    onSuccess: (r) => navigate(`/warehouse/transfers/${r.id}`),
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New transfer</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField select label="From warehouse *" size="small" sx={{ minWidth: 240 }}
            value={sourceWarehouseId} onChange={(e) => setSourceWarehouseId(e.target.value)}>
            <MenuItem value="">— pick source —</MenuItem>
            {warehouses.data?.items.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField select label="To warehouse *" size="small" sx={{ minWidth: 240 }}
            value={destWarehouseId} onChange={(e) => setDestWarehouseId(e.target.value)}>
            <MenuItem value="">— pick destination —</MenuItem>
            {warehouses.data?.items.filter(w => w.id !== sourceWarehouseId).map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField label="Expected delivery" type="date" size="small"
            InputLabelProps={{ shrink: true }}
            value={expectedDeliveryDate} onChange={(e) => setExpectedDeliveryDate(e.target.value)} />
        </Stack>

        {!sourceWarehouseId && (
          <Alert severity="info" sx={{ mb: 2 }}>Pick a source warehouse to populate the picker.</Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Lines</Typography>
          <Button startIcon={<AddIcon />} onClick={() => setLines(ls => [...ls, blank()])} size="small">
            Add line
          </Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 320 }}>Item *</TableCell>
              <TableCell>Batch id *</TableCell>
              <TableCell align="right" sx={{ width: 130 }}>Qty *</TableCell>
              <TableCell sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, idx) => (
              <TableRow key={l.key}>
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={picker.data ?? []}
                    getOptionLabel={(o) => `${o.sku} — ${o.name} (avail ${o.availableQty})`}
                    value={l.item}
                    onChange={(_, v) => update(l.key, { item: v })}
                    renderInput={(p) => <TextField {...p} placeholder={`Line ${idx + 1}`} />}
                    loading={picker.isLoading}
                  />
                </TableCell>
                <TableCell>
                  <TextField size="small" placeholder="Batch UUID" value={l.batchId} fullWidth
                    onChange={(e) => update(l.key, { batchId: e.target.value })} />
                </TableCell>
                <TableCell align="right">
                  <TextField type="number" size="small" sx={{ width: 110 }}
                    value={l.qty} onChange={(e) => update(l.key, { qty: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: "0.001", style: { textAlign: "right" } }} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => remove(l.key)} disabled={lines.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ my: 3 }} />
        <TextField label="Notes" size="small" fullWidth multiline minRows={2}
          value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ mb: 2 }} />

        {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button onClick={() => navigate("/warehouse/transfers")}>Cancel</Button>
          <Button variant="contained" disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>
            Save draft transfer
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
